import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
} from '@angular/core';
import { VehicleEntity } from '@core/store/entities/vehicle/vehicle.entity';
import { PremiumEntity } from '@core/store/entities/premium/premium.entity';
import { CoverageEntity } from '@core/store/entities/coverage/coverage.entity';
import { ProductModel } from '@core/store/entities/product/product.model';
import { CoveredLocationEntity } from '@core/store/entities/covered-location/covered-location.reducer';
import { Nullable } from '@shared/utils/type.utils';
import {
  VehicleEnrollment,
  MobileEnrollment,
} from '@core/store/entities/telematics/telematics.model';
import { Observable, combineLatest } from 'rxjs';
import { CoveragesService } from '@core/services/coverages.service';
import { CoveredLocationService } from '@core/services/covered-location.service';
import { ProductsService } from '@core/services/products.service';
import { TelematicsService } from '@core/services/telematics.service';
import { VehicleService } from '@core/services/vehicle.service';
import { PremiumService } from '@core/services/premium.service';
import { Dictionary } from '@ngrx/entity';
import { LayoutService } from '@core/services/layout.service';
import { ErrorModel } from '@entities/error/error.model';
import { ErrorService } from '@entities/error/error.service';
import { BillingFormService } from '@forms-store/services/billing-form.service';
import { PriceSummaryDisplayContent } from '@app/hub/components/price-summary-display/price-summary-display-content';
import { Store } from '@ngrx/store';
import { UwIssuesSelectors } from '@core/store/selectors';
import { map } from 'rxjs/operators';
import { ProductHelper } from '@core/helper/product.helper';
import { ProductUtils } from '@shared/utils/product.util';
import { UwIssuesByProduct } from '@entities/uw-issues/uw-issues.selector';

@Component({
    selector: 'nwx-coverage-display-container',
    templateUrl: './coverage-display-container.component.html',
    styleUrls: ['./coverage-display-container.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CoverageDisplayContainerComponent {
  @Output() editTelematicsClicked = new EventEmitter();

  vehicles$: Observable<VehicleEntity[]>;
  premiums$: Observable<PremiumEntity[]>;
  coverages$: Observable<CoverageEntity[]>;
  products$: Observable<ProductModel[]>;
  coveredLocations$: Observable<Nullable<Dictionary<CoveredLocationEntity>>>;
  enrollment$: Observable<Nullable<VehicleEnrollment | MobileEnrollment>>;
  maxSizeMini$: Observable<boolean>;
  errors$: Observable<ErrorModel[]>;
  priceSummaryDisplayContent$: Observable<PriceSummaryDisplayContent>;

  constructor(
    private vehicleService: VehicleService,
    private premiumService: PremiumService,
    private coverageService: CoveragesService,
    private productsService: ProductsService,
    private coveredLocationService: CoveredLocationService,
    private telematicsService: TelematicsService,
    private layoutService: LayoutService,
    private errorService: ErrorService,
    private billingFormService: BillingFormService,
    private store: Store
  ) {
    this.vehicles$ = this.vehicleService.getAllVehicles();
    this.premiums$ = this.premiumService.getAllPremiums();
    this.coverages$ = this.coverageService.getAllQuoteCoveragesByVersion();
    this.products$ = combineLatest([
      this.productsService.getSelectedProducts(),
      this.productsService.getProduct('DwellingFire'),
      this.store.select(UwIssuesSelectors.getUwIssuesByProduct),
    ]).pipe(
      map(([selectedProducts, dwellingFire, uwIssues]) =>
        this.addDwellingFireIfBlocked(selectedProducts, dwellingFire, uwIssues)
      )
    );
    this.coveredLocations$ =
      this.coveredLocationService.getAllCoveredLocations();
    this.enrollment$ = this.telematicsService.getCurrentEnrollment();
    this.maxSizeMini$ = this.layoutService.maxSizeMini();
    this.errors$ = this.errorService.getErrors();
    this.priceSummaryDisplayContent$ =
      this.billingFormService.getPriceSummaryDisplayContentInputs();
  }

  onTelematicsClicked(): void {
    this.editTelematicsClicked.emit();
  }

  private addDwellingFireIfBlocked(
    selectedProducts: ProductModel[],
    dwellingFire: Nullable<ProductModel>,
    uwIssues: UwIssuesByProduct
  ): ProductModel[] {
    if (
      dwellingFire &&
      ProductHelper.hasBlockedByPropertyIssue(
        'DwellingFire',
        uwIssues.DwellingFire
      ) &&
      !selectedProducts.some((p) => p.type === 'DwellingFire') &&
      selectedProducts.some((p) => ProductUtils.isPropertyProduct(p.type))
    ) {
      return [...selectedProducts, dwellingFire];
    }
    return selectedProducts;
  }
}
